import { CommonModule } from '@angular/common';
import {
    Component, EventEmitter, Input, NgZone, OnDestroy, OnInit, Output,
} from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';

import { ProgressSpinnerModule } from 'primeng/progressspinner';

/** What the embed page posts back once a card is entered. */
export interface BlueSageCardToken {
    cardToken: string;
    cardBrand: string | null;
    last4: string | null;
}

/** The message types the embed page sends. Anything else is ignored. */
const CARD_TOKEN_MESSAGE = 'bluesage:card-token';
const RESIZE_MESSAGE = 'bluesage:resize';

/**
 * Enough for the whole form before the iframe reports its own height.
 *
 * Raised from 260 after testing on 2026-08-28: BlueSage's page does not always
 * send `bluesage:resize`, so the initial figure is frequently the FINAL one, and
 * at 260 the card form opened with its own scrollbar and the submit button below
 * the fold. Over-tall costs a little whitespace on a page that resizes itself;
 * under-tall hides the button that completes the payment.
 */
const INITIAL_HEIGHT_PX = 460;
/** A hostile or broken page must not be able to push the layout off-screen. */
const MAX_HEIGHT_PX = 900;

/**
 * The floor a resize message cannot take the frame below.
 *
 * The bound was one-sided, so an early `bluesage:resize` of a few pixels —
 * posted before the embed page's layout settles — collapsed the iframe to a
 * sliver, and nothing restored it unless a second message happened to arrive.
 * The page does not always send one.
 */
const MIN_HEIGHT_PX = 260;

/**
 * BlueSage's embedded card fields.
 *
 * **This component renders an iframe and nothing else.** It never draws a card
 * input, never reads one, and never has access to what is typed into one. The
 * Passage script does not load in this document; BlueSage serves the page, and
 * what comes back is an opaque one-time `bsp_card_…`.
 *
 * That is the hard constraint behind the whole of Phase B, not a stylistic
 * choice: a card form this application hosts — even one iframing each field
 * individually — moves MoveRight from SAQ A into SAQ A-EP and a different
 * audit. If a future change makes it tempting to "just read the card number
 * here", that change is the thing to refuse.
 *
 * ## The origin check is the security boundary
 *
 * `window.addEventListener('message')` receives from **any** frame on the page,
 * including one an attacker got in through an ad, an extension, or an XSS
 * elsewhere. A listener that trusts `event.data` without checking
 * `event.origin` will accept a card token — or a fake one — from whoever shouts
 * loudest.
 *
 * So the origin is compared against the **embed URL we were given**, not
 * against a hard-coded constant: the sandbox and production are served from
 * different hosts, and a constant would either be wrong in one environment or
 * loose enough to match both.
 */
@Component({
    selector: 'app-bluesage-card-fields',
    standalone: true,
    imports: [ CommonModule, ProgressSpinnerModule ],
    templateUrl: './bluesage-card-fields.component.html',
    styleUrls: [ './bluesage-card-fields.component.scss' ],
})
export class BlueSageCardFieldsComponent implements OnInit, OnDestroy {

    /** The BlueSage-served URL to frame. Single use, 15 minutes. */
    @Input() set embedUrl(value: string | null | undefined) {
        this.expectedOrigin = originOf(value);
        this.safeUrl = value && this.expectedOrigin
            ? this.sanitizer.bypassSecurityTrustResourceUrl(value)
            : null;
    }

    /** A card was entered and BlueSage handed back a one-time token. */
    @Output() cardTokenReceived = new EventEmitter<BlueSageCardToken>();

    safeUrl: SafeResourceUrl | null = null;
    heightPx = INITIAL_HEIGHT_PX;

    /**
     * The ONLY origin whose messages are trusted, derived from the embed URL.
     *
     * Null means we were given nothing framable, in which case no listener runs
     * and nothing can be accepted.
     */
    private expectedOrigin: string | null = null;

    private listener?: (event: MessageEvent) => void;

    constructor(
        private sanitizer: DomSanitizer,
        private zone: NgZone,
    ) {}

    ngOnInit(): void {
        this.listener = (event: MessageEvent) => this.onMessage(event);
        window.addEventListener('message', this.listener);
    }

    ngOnDestroy(): void {
        // A listener that outlives the dialog would keep accepting card tokens
        // for a form nobody is looking at.
        if (this.listener) { window.removeEventListener('message', this.listener); }
    }

    /**
     * Handle one message, having first established it is ours.
     *
     * Every guard here is deliberate and the order matters: origin, then shape,
     * then content. Checking the payload first would mean reasoning about a
     * hostile frame's data before deciding whether to listen to it at all.
     */
    private onMessage(event: MessageEvent): void {
        // 1. The security boundary. Nothing below this line runs for a frame we
        //    did not open.
        if (!this.expectedOrigin || event.origin !== this.expectedOrigin) { return; }

        const data = event.data as Record<string, unknown> | null;
        if (!data || typeof data !== 'object') { return; }

        if (data.type === RESIZE_MESSAGE) {
            this.applyHeight(data.height);
            return;
        }

        if (data.type !== CARD_TOKEN_MESSAGE) { return; }

        const cardToken = typeof data.card_token === 'string' ? data.card_token : null;
        // A "success" carrying no token is not a success. Emitting one would
        // send the dialog on to charge nothing.
        if (!cardToken) { return; }

        // The message arrives outside Angular's zone, so an emit from here would
        // update state without triggering change detection — the dialog would
        // have the token and the screen would not move.
        this.zone.run(() => this.cardTokenReceived.emit({
            cardToken,
            cardBrand: typeof data.card_brand === 'string' ? data.card_brand : null,
            last4: typeof data.last4 === 'string' ? data.last4 : null,
        }));
    }

    /** Grow to fit the form, within a bound the page controls rather than the frame. */
    private applyHeight(height: unknown): void {
        if (typeof height !== 'number' || !Number.isFinite(height) || height <= 0) { return; }

        this.zone.run(() => {
            this.heightPx = Math.min(
                Math.max(Math.round(height), MIN_HEIGHT_PX),
                MAX_HEIGHT_PX,
            );
        });
    }
}

/**
 * The origin of a URL, or null if it is not one we can frame.
 *
 * Returning null for anything unparseable is what makes "no expected origin"
 * mean "trust nothing" rather than "trust everything".
 */
function originOf(url: string | null | undefined): string | null {
    if (!url) { return null; }

    try {
        const parsed = new URL(url);
        return parsed.protocol === 'https:' || parsed.protocol === 'http:' ? parsed.origin : null;
    } catch {
        return null;
    }
}
